/* eslint-disable max-lines */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/exhaustive-deps */
import * as React from 'react';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useResizeDetector } from 'react-resize-detector';
import { LocalVideoTrack, RoomEvent, Track, TrackEvent } from 'livekit-client';
import type {
  MessageDecoder,
  MessageEncoder,
  TrackReferenceOrPlaceholder,
  WidgetState,
} from '@livekit/components-core';
import {
  isEqualTrackRef,
  isTrackReference,
  isWeb,
  log,
} from '@livekit/components-core';
import {
  CarouselLayout,
  Chat,
  ConnectionStateToast,
  ControlBar,
  FocusLayoutContainer,
  GridLayout,
  LayoutContextProvider,
  MessageFormatter,
  useCreateLayoutContext,
  useLocalParticipant,
  usePinnedTracks,
  useRoomContext,
  useTracks,
} from '@livekit/components-react';
import { BackgroundBlur, VirtualBackground } from '@livekit/track-processors';
import { useSpeedTest } from '../../hooks/useSpeedTest';
import { setIsAdmin } from '../../redux/slices/auth';
import {
  selectStreamGoRoom,
  selectIsPresentGo,
  selectLiveNowUsers,
  selectAudioOutputDeviceId,
  selectQueryString,
  selectHasBackgroundBlur,
  selectHasVirtualBackground,
  selectVirtualBackground,
} from '../../redux/slices/room';
import { useGetUserQuery } from '../../redux/streamGoApi';
import { UserRole } from '../../redux/streamGoApi/enums';
import { ConferenceAudioRenderer } from './ConferenceAudioRenderer';
import { FocusLayout } from './FocusLayout';
import { ParticipantTile } from './ParticipantTile';
import { getWindowSizeClass } from './utils';
import { WidgetManager } from './WidgetManager';

export interface IVideoConferenceProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /** @alpha */
  SettingsComponent?: (props: any) => ReactNode;
  chatMessageDecoder?: MessageDecoder;
  chatMessageEncoder?: MessageEncoder;
  chatMessageFormatter?: MessageFormatter;
  setCurrentUserLive: (useLive: boolean) => void;
}

export function VideoConference({
  chatMessageDecoder,
  chatMessageEncoder,
  chatMessageFormatter,
  setCurrentUserLive,
  SettingsComponent,
  ...props
}: IVideoConferenceProps) {
  const dispatch = useDispatch();
  const room = useRoomContext();
  const [localSid, setLocalSid] = useState<string>();
  const liveNowUsers = useSelector(selectLiveNowUsers);
  const { chat, hq, lowcpumode, screenshare, video } =
    useSelector(selectQueryString);
  const outputDeviceId = useSelector(selectAudioOutputDeviceId);
  const hasBackgroundBlur = useSelector(selectHasBackgroundBlur);
  const hasVirtualBackground = useSelector(selectHasVirtualBackground);
  const virtualBackgroundImage = useSelector(selectVirtualBackground);
  const { data: user } = useGetUserQuery();
  const isAdmin = user?.Role === UserRole.Admin;

  useEffect(() => {
    dispatch(setIsAdmin(isAdmin));
  }, [isAdmin]);

  const isPresentGo = useSelector(selectIsPresentGo);
  const sgRoom = useSelector(selectStreamGoRoom);
  const [windowWidth, setWindowWidth] = useState(1000);
  const { ref: sizeRef, width = 1000 } = useResizeDetector();

  useEffect(() => {
    if (outputDeviceId) {
      room.switchActiveDevice('audiooutput', outputDeviceId);
    }
  }, [outputDeviceId]);

  useEffect(() => {
    const clientWidth = sizeRef?.current?.clientWidth;
    if (clientWidth) {
      setWindowWidth(clientWidth);
    }
  }, [width]);

  const [widgetState, setWidgetState] = React.useState<WidgetState>({
    showChat: false,
    unreadMessages: 0,
    showSettings: false,
  });
  const { showChat, showSettings } = widgetState;
  const sideBarOpen = showChat || showSettings;

  const { cameraTrack, localParticipant } = useLocalParticipant();

  useEffect(() => {
    const track = cameraTrack?.videoTrack;
    if (!hq || !track) return undefined;

    const setContentHint = () => {
      track.mediaStreamTrack.contentHint = 'text';
    };
    setContentHint();
    track.on(TrackEvent.Restarted, setContentHint);

    return () => {
      track.off(TrackEvent.Restarted, setContentHint);
    };
  }, [cameraTrack?.videoTrack, hq]);

  const localSgParticipant = sgRoom?.Participants.find(
    participant => participant.ParticipantId === localSid,
  );

  const { startTest } = useSpeedTest({
    participantId: localSid,
  });

  useEffect(() => {
    let interval = 0;

    interval = window.setInterval(() => {
      if (localParticipant.sid) {
        setLocalSid(localParticipant.sid);
        clearInterval(interval);
      }
    }, 1000);
  }, []);

  const doSpeedTest = useCallback(() => {
    if (
      localSid &&
      (localSgParticipant?.DownloadSpeed === null ||
        localSgParticipant?.UploadSpeed === null ||
        localSgParticipant?.DownloadSpeed === 0 ||
        localSgParticipant?.UploadSpeed === 0)
    ) {
      startTest(5);
    }
  }, [
    localSid,
    localSgParticipant?.DownloadSpeed,
    localSgParticipant?.UploadSpeed,
  ]);

  useEffect(() => {
    let interval = 0;
    doSpeedTest();

    interval = window.setInterval(() => {
      doSpeedTest();
    }, 30000);

    return () => clearInterval(interval);
  }, [doSpeedTest]);

  useEffect(() => {
    if (localSid) {
      window.setTimeout(() => {
        startTest(5);
      }, 5000);
    }
  }, [localSid]);

  useEffect(() => {
    setCurrentUserLive(
      Boolean(liveNowUsers.find(x => x === localParticipant.sid)),
    );
  }, [localParticipant.sid, liveNowUsers]);

  const filterTracks = (track: TrackReferenceOrPlaceholder) => {
    if (track.participant.isLocal) {
      return true;
    }
    const identity = track.participant.identity.toLowerCase();
    if (identity.startsWith('liveoutputpreview')) {
      return false;
    }
    if (track.participant.name?.startsWith('altViewMod')) {
      return false;
    }
    if (lowcpumode) {
      return track.participant.sid === localParticipant.sid;
    }
    if (!isAdmin) {
      return !identity.startsWith('sg_vm');
    }
    return true;
  };

  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { updateOnlyOn: [RoomEvent.ActiveSpeakersChanged], onlySubscribed: false },
  ).filter(filterTracks);

  useEffect(() => {
    if (cameraTrack?.videoTrack instanceof LocalVideoTrack) {
      const currentProcessor = cameraTrack?.videoTrack?.getProcessor();

      if (hasVirtualBackground && virtualBackgroundImage) {
        const virtualBackground = VirtualBackground(virtualBackgroundImage);
        cameraTrack?.videoTrack.setProcessor(virtualBackground);
      } else if (hasBackgroundBlur) {
        const blur = BackgroundBlur(20, { delegate: 'CPU' });
        cameraTrack?.videoTrack.setProcessor(blur);
      } else if (currentProcessor) {
        cameraTrack.videoTrack.stopProcessor();
      }
    }
  }, [
    hasBackgroundBlur,
    hasVirtualBackground,
    cameraTrack?.videoTrack,
    virtualBackgroundImage,
  ]);

  const widgetUpdate = React.useCallback((state: WidgetState) => {
    log.debug('updating widget state', state);
    setWidgetState(state);
  }, []);

  const layoutContext = useCreateLayoutContext();

  const focusTrack = usePinnedTracks(layoutContext)?.[0];
  const carouselTracks = tracks.filter(
    track => !isEqualTrackRef(track, focusTrack),
  );

  React.useEffect(() => {
    if (focusTrack && !isTrackReference(focusTrack)) {
      const updatedFocusTrack = tracks.find(
        tr =>
          tr.participant.identity === focusTrack.participant.identity &&
          tr.source === focusTrack.source,
      );
      if (
        updatedFocusTrack !== focusTrack &&
        isTrackReference(updatedFocusTrack)
      ) {
        layoutContext.pin.dispatch?.({
          msg: 'set_pin',
          trackReference: updatedFocusTrack,
        });
      }
    }
  }, [focusTrack?.publication?.trackSid, tracks]);

  return (
    <div
      className={`lk-video-conference ${
        sideBarOpen ? 'side-bar-open' : ''
      } ${getWindowSizeClass(windowWidth)}`}
      {...props}
      ref={sizeRef}
    >
      {isWeb() && (
        <LayoutContextProvider
          onWidgetChange={widgetUpdate}
          value={layoutContext}
        >
          <WidgetManager widgetState={widgetState} />
          <div className="lk-video-conference-inner">
            {!focusTrack ? (
              <div className="lk-grid-layout-wrapper">
                <GridLayout tracks={tracks}>
                  <ParticipantTile />
                </GridLayout>
              </div>
            ) : (
              <div className="lk-focus-layout-wrapper">
                <FocusLayoutContainer>
                  <CarouselLayout tracks={carouselTracks}>
                    <ParticipantTile />
                  </CarouselLayout>
                  {focusTrack && <FocusLayout trackRef={focusTrack} />}
                </FocusLayoutContainer>
              </div>
            )}
            <ControlBar
              controls={{
                chat: !isPresentGo && chat,
                leave: !isPresentGo,
                settings: !!SettingsComponent,
                screenShare: screenshare,
                camera: video,
              }}
              variation={
                windowWidth < 1000 || sideBarOpen ? 'minimal' : undefined
              }
            />
          </div>
          <Chat
            messageDecoder={chatMessageDecoder}
            messageEncoder={chatMessageEncoder}
            messageFormatter={chatMessageFormatter}
            style={{
              display:
                widgetState.showChat && !widgetState.showSettings
                  ? 'grid'
                  : 'none',
            }}
          />
          {SettingsComponent && (
            <div
              className="lk-chat lk-settings"
              style={{ display: widgetState.showSettings ? 'block' : 'none' }}
            >
              <SettingsComponent />
            </div>
          )}
        </LayoutContextProvider>
      )}
      <ConferenceAudioRenderer />
      <ConnectionStateToast />
    </div>
  );
}
